import { getNodeImageUrl } from "../canvasUtils";
import { getSeedance25ReferenceMediaType, getSeedance25ReferenceUrl } from '../../../utils/seedance25Capabilities';
import { getNodeOutputType } from './connectionRules';

// Separate from the legacy image-preview strip: audio/video and original URLs
// must remain available to the official library controls without changing it.
export function buildConnectedModelArkReferenceIndex(edgeList = [], nds = []) {
  const nodes = new Map(nds.map((node) => [String(node.id), node]));
  const index = new Map();
  for (const edge of edgeList) {
    if (edge.data?.connectionType !== 'reference') continue;
    const node = nodes.get(String(edge.source));
    if (!node || getNodeOutputType(node) === 'text') continue;
    const url = getSeedance25ReferenceUrl(node.data) || getNodeImageUrl(node.data);
    if (!url) continue;
    const outputType = getNodeOutputType(node);
    const mediaType = ['video', 'audio'].includes(outputType) ? outputType : getSeedance25ReferenceMediaType(node.data);
    const target = String(edge.target);
    const references = index.get(target) || [];
    if (!references.some((item) => item.url === url && item.mediaType === mediaType)) references.push({ url, mediaType });
    index.set(target, references);
  }
  return index;
}

// 获取节点的逻辑类型（兼容 atom 节点）
// atom 节点的 node.type === 'atom'，真实类型存在 data.legacyType
export function getNodeKind(node) {
  return node.data?.legacyType || node.type;
}

// 节点是否是指定逻辑类型
export function isNodeType(node, type) {
  return getNodeKind(node) === type;
}

// 节点是否有可用的图片 URL
export function getNodeUrl(node) {
  const d = node.data || {};
  return d.url || d.result || d.generatedImage || d.generatedVideo || d.thumbnail || null;
}

// 一次建立全部下游节点的参考图索引，避免每个节点重复扫描全部边和节点。
export function buildConnectedImageIndex(edgeList, nds) {
  const nodeById = new Map((nds || []).map((node) => [String(node.id), node]));
  const incomingByTarget = new Map();

  (edgeList || []).forEach((edge) => {
    if (edge.data?.connectionType !== 'reference') return;
    const targetId = String(edge.target);
    const incoming = incomingByTarget.get(targetId) || [];
    incoming.push(edge);
    incomingByTarget.set(targetId, incoming);
  });

  const imageIndex = new Map();
  incomingByTarget.forEach((incomingEdges, targetId) => {
    const edgesBySource = new Map();
    incomingEdges.forEach((edge) => {
      const sourceId = String(edge.source);
      const sourceEdges = edgesBySource.get(sourceId) || [];
      sourceEdges.push(edge);
      edgesBySource.set(sourceId, sourceEdges);
    });

    const images = [];
    edgesBySource.forEach((sourceEdges, sourceId) => {
      const peer = nodeById.get(sourceId);
      if (!peer || peer.data?.resourceType === 'audio') return;
      const url = getNodeImageUrl(peer.data);
      if (!url) return;
      images.push({
        url,
        name: peer.data?.name || (peer.data?.shotNumber ? `分镜#${peer.data.shotNumber}` : '参考图'),
        assetType: peer.data?.assetType || null,
        sourceNodeId: sourceId,
        edgeIds: sourceEdges.map((edge) => edge.id),
      });
    });
    imageIndex.set(targetId, images);
  });

  return imageIndex;
}

// 收集某个节点通过 incoming reference 边关联的上游图片。
export function collectConnectedImages(nodeId, edgeList, nds) {
  return buildConnectedImageIndex(edgeList, nds).get(String(nodeId)) || [];
}

// 节点是否需要 connectedImages 注入
// 所有 atom 节点都需要（resource 也可能被连线作为下游）
export function needsConnectedImages(nodeType) {
  return (
    nodeType === 'resource' ||
    nodeType === 'generate' ||
    nodeType === 'storyboardGrid' ||
    nodeType === 'storyboardShot' ||
    nodeType === 'seedanceMerge' ||
    nodeType === 'storyboardEpisode' ||
    nodeType === 'scriptToStoryboard' ||
    nodeType === 'motionStoryboard'
  );
}
